import { ShipmentBox } from "./Shipments/ShipmentBoxes";

export interface Shipments {
  isSelected: boolean;
  id: number;
  coupon_id?: string;
  inventory_id?: number;
  campaign_id?: number;
  order_id?: any;
  shipment_type: number;
  shipment_type_label?: string;
  shopify_order_id?: number;
  fulfilled_on_shopify?: boolean;
  first_name: string;
  last_name?: string;
  customer_name?: string;
  email: string;
  calling_code: string;
  phone: string;
  address1: string;
  address2?: string;
  address3?: string;
  landmark?: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  status?: number;
  status_label?: string;
  paid?: boolean;
  placed_by?: number;
  place_by_first_name?: string;
  place_by_last_name?: string;
  awb?: string;
  awb_track_url?: string;
  order_notes?: string;
  dispatch_date: string;
  active: boolean;
  cancelled: boolean;
  /** 1/true when same customer already had matching email/phone within 6 months at create. */
  is_duplicate?: number | boolean;
  feedback_message?: string;
  length?: string | number;
  breadth?: string | number;
  height?: string | number;
  weight?: string | number;
  alternate_email_id?: string;
  alternate_phone?: string;
  dispatch_status?: number;
  placed_by_admin_id?: number;
  placed_by_admin_name?: string;
  employee_id?: number;
  delivery_partner_id?: string | number;
  shipping_cost?: number;
  packs_label?: string;
  pack_products_label?: string;
  /** Active product lines on the shipment (list API only, not in CSV). */
  products_count?: number;
  expected_delivery_date: string;
  delivered_status_date?: string;
  status_change_date?: string;
  shipped_status_date?: string;
  created_date: string;
  delivery_partner_name?: string;
  shipment_method?: ShipmentMethod;
  booking_type?: ShipmentBookingType | string | null;
  booking_state?: string | null;
  last_booking_error?: string | null;
  last_booked_at?: string | null;
  booking_attempts?: number | null;
  carrier_ref_no?: string | null;
  modified_date: string;
  modified_by_id?: number;
  modified_by_name?: string;
  inventory_type: number;
  size_from_recipient?: string;
  declared_value?: number | null;
  label_s3_key?: string | null;
  mps_label_s3_key?: string | null;
  boxes?: ShipmentBox[];
}
export interface ShipmentsDetails {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  order_notes?: string;
  address1?: string;
  address2?: string;
  landmark?: string;
  country?: string;
  state?: string;
  city?: string;
  pincode?: string;
  phone?: string;
  alternate_phone?: string;
  expected_delivery_date?: string;
  delivered_status_date?: string;
  status_change_date?: string;
  shipped_status_date?: string;
  delivery_partner_id?: string | number;
  delivery_partner_name?: string;
  dispatch_date?: string;
  status?: number;
  status_label?: string;
  awb?: string;
  awb_track_url?: string;
  additional_awb?: string;
  shipping_cost?: number;
  length?: string | number;
  breadth?: string | number;
  height?: string | number;
  weight?: string | number;
  shipment_method?: ShipmentMethod;
  booking_type?: ShipmentBookingType | string | null;
  declared_value?: number | null;
}
export enum ShipmentMethod {
  RAIL = "RAIL",
  SURFACE = "SURFACE",
  AIR = "AIR",
}
export enum ShipmentBookingType {
  REMOTE = "remote",
  BULK = "bulk",
}

export const SHIPMENT_BOOKING_TYPE_OPTIONS: Array<{
  id: ShipmentBookingType;
  value: string;
}> = [
  { id: ShipmentBookingType.REMOTE, value: "Remote" },
  { id: ShipmentBookingType.BULK, value: "Bulk" },
];

export function formatShipmentBookingTypeLabel(
  bookingType?: ShipmentBookingType | string | null,
): string {
  try {
    if (!bookingType || !String(bookingType).trim()) {
      return "—";
    }

    const normalized = String(bookingType).trim().toLowerCase();
    if (normalized === ShipmentBookingType.REMOTE) {
      return "Remote";
    }
    if (normalized === ShipmentBookingType.BULK) {
      return "Bulk";
    }

    return "—";
  } catch {
    return "—";
  }
}

export function parseShipmentBookingType(
  bookingType?: ShipmentBookingType | string | null,
): ShipmentBookingType | undefined {
  try {
    if (!bookingType || !String(bookingType).trim()) {
      return undefined;
    }

    const normalized = String(bookingType).trim().toLowerCase();
    if (normalized === ShipmentBookingType.REMOTE) {
      return ShipmentBookingType.REMOTE;
    }
    if (normalized === ShipmentBookingType.BULK) {
      return ShipmentBookingType.BULK;
    }

    return undefined;
  } catch {
    return undefined;
  }
}

export interface DeliveryPartner {
  active: number;
  bank_details_link?: string;
  company_website_link?: string | null;
  created_by: number;
  created_date: string;
  delivery_partner_id: number;
  delivery_partner_name: string;
  email?: string;
  gst_number?: string;
  gstin_certificate_link?: string;
  is_logistics_vendor?: number;
  is_printer?: number;
  is_product_supplier?: number;
  modified_date: string;
  msme_link?: string | null;
  pan_card_link?: string;
  pan_number?: string;
  payment_term?: string;
  products_catalog_link?: string | null;
}
export interface BulkUpdate {
  all_selected?: boolean;
  shipment_ids?: number[];
  length?: number;
  breadth?: number;
  height?: number;
  weight?: number;
  status?: number;
  delivery_partner_id?: number;
  shipment_method?: ShipmentMethod;
  booking_type?: ShipmentBookingType;
  /** Required when status is CANCELLED (8). */
  cancelReason?: string;
  /** Monetary declared value. Null clears; must be > 0 when set. */
  declared_value?: number | null;
}

export function parseDeclaredValue(
  value: unknown,
):
  | { ok: true; declaredValue: number | null }
  | { ok: false; message: string } {
  try {
    if (value === "" || value == null) {
      return { ok: true, declaredValue: null };
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return { ok: false, message: "Declared value is invalid." };
    }
    if (parsed <= 0) {
      return {
        ok: false,
        message: "Declared value must be greater than 0, or left empty.",
      };
    }
    return { ok: true, declaredValue: parsed };
  } catch {
    return { ok: false, message: "Declared value is invalid." };
  }
}
export interface StatusType {
  id: number;
  value: string;
}

export interface TrackingBulkUpdateSkipped {
  shipmentId: number;
  rowNumber: number;
  reason: string;
}

/** `200` (≤ 500 rows) applies inline; `202` (> 500 rows) returns a `jobId` to poll. */
export interface TrackingBulkUpdateResult {
  shipmentIds?: number[];
  updatedCount?: number;
  skippedCount?: number;
  skipped?: TrackingBulkUpdateSkipped[];
  jobId?: number;
  totalCount?: number;
}

export interface TrackingBulkUpdateJobStatus {
  jobId: number;
  progress: number;
  finished: boolean;
  success: boolean;
  summary: string | null;
  startTime: string | null;
  endTime: string | null;
}

export type BulkShippingCostSkipCode =
  | "NOT_FOUND"
  | "CANCELLED"
  | "STATUS_NOT_ALLOWED"
  | "BOOKED";

export interface BulkShippingCostSkipped {
  shipmentId: number;
  code: BulkShippingCostSkipCode;
  reason: string;
}

export interface BulkUpdateShippingCostResult {
  shipmentIds: number[];
  updatedCount: number;
  skippedCount: number;
  skipped: BulkShippingCostSkipped[];
  shipping_cost: number;
}

/** Same limit as the backend bulk shipping cost API. */
export const MAX_BULK_SHIPMENT_IDS = 500;

export const parseBulkShippingCost = (
  value: string,
): { ok: true; cost: number } | { ok: false; message: string } => {
  const trimmed = value.trim();
  if (trimmed === "") {
    return { ok: false, message: "Enter a shipping cost." };
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { ok: false, message: "Shipping cost must be a number of 0 or more." };
  }
  return { ok: true, cost: parsed };
};

export interface Swagpack {
  active: number;
  blocked_quantity: number | null;
  used_quantity: number | 0;
  created_by_name: string;
  created_date: string;
  quantity?: number | string;
  description: string | null;
  id: number;
  inventory_id: number;
  label: string;
  pack_image_url: string;
  products: PackProduct[];
  created_by: number;
  modified_date: string;
}

export interface PackProduct {
  blocked_quantity: number;
  description: string;
  pack_id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  image_url?: string;
  variants?: PackProductVariant[];
  created_date: string;
  modified_date: string;
  selectedVariant?: PackProductVariant[];
  images?: string;
}

export interface PackProductVariant {
  blocked_quantity: number;
  client_sku?: string | null;
  color?: string;
  id: number;
  initial_quantity: number;
  pack_id: number;
  product_id: number;
  quantity: number;
  quantity_allocated_to_shopify?: number;
  quantity_last_synced_to_shopify?: number;
  shopify_variant_id?: string | null;
  size: string;
  total_quantity: number | null;
  created_date: string;
  modified_date: string;
}

export interface TabWiseShipmentsCount {
  tab_id: string;
  label: string;
  requests_count: number;
}
